import React from 'react';
import clsx from 'clsx';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import IconArrowDown from '@/assets/icons/icon-arrow-down-01.svg?react';
import IconPaintBoard from '@/assets/icons/icon-paint-board.svg?react';
import { ColorStyle } from '@/components/panels/styles/ColorStyle/ColorStyle';
import type { StyleColors } from '@builder4/shared/types/styles';

interface StylesColorStylesProps {
  className?: string;
  showInnerHeader?: boolean;
  onClick?: () => void;
  isOpenCollapse?: boolean;
  onDataChange: (data: StyleColors) => void;
  value: StyleColors;
}

// Presentational Color Styles list for the Styles panel (no Craft dependencies)
export const StylesColorStyles: React.FC<StylesColorStylesProps> = ({
  className = '',
  showInnerHeader = true,
  onClick,
  isOpenCollapse = false,
  onDataChange,
  value: colorStyleData,
}) => {
  const handleClick = () => {
    if (onClick) onClick();
  };

  const commitRename = (colorKey: string, newRaw: string) => {
    const trimmed = (newRaw || '').trim();
    if (!trimmed) return;
    const prev = colorStyleData;
    if (!prev[colorKey]) return;
    const next = { ...prev } as StyleColors;
    next[colorKey] = { ...next[colorKey], name: trimmed } as (typeof next)[string];
    onDataChange(next);
  };

  const list = (
    <div className="mt-3 flex flex-col items-start gap-3 pb-3">
      {Object.entries(colorStyleData).map(([colorKey, colorObj]) => {
        const label = colorObj.name;
        return (
          <ColorStyle
            key={colorKey}
            className="w-full px-2"
            label={label}
            value={colorObj.value}
            onRename={(newLabel) => commitRename(colorKey, newLabel || '')}
            onChange={(payload) => {
              const current = colorStyleData[colorKey];
              if (!current) return;
              const next = { ...colorStyleData } as StyleColors;
              next[colorKey] = {
                ...current,
                value: payload,
              } as (typeof next)[string];
              onDataChange(next);
            }}
          />
        );
      })}
    </div>
  );

  return (
    <div className={clsx('', className)}>
      {showInnerHeader ? (
        <Collapsible defaultOpen={isOpenCollapse} className="bg-neutral-0">
          <CollapsibleTrigger
            onClick={handleClick}
            className={clsx(
              'group bg-neutral-0 w-full cursor-pointer border-t border-neutral-100 px-2 py-4',
            )}
          >
            <div className="flex w-full items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <IconPaintBoard className="h-4 w-4 text-neutral-900" />
                <div className="flex items-center gap-1">
                  <span className="text-button-sm text-neutral-900">Color Styles</span>
                </div>
              </div>
              <IconArrowDown className="h-4 w-4 text-neutral-500 transition-all" />
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>{list}</CollapsibleContent>
        </Collapsible>
      ) : (
        list
      )}
    </div>
  );
};

export default StylesColorStyles;
